import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SignatureCanvas from 'react-signature-canvas';
import InlinePdfViewer from './InlinePdfViewer';
import { colors, layout } from '../styles';
import { toast } from 'react-toastify';

interface SignatureBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (signatureDataUrl: string) => Promise<void>;
  title?: string;
  submitLabel?: string;
  previewPdfUrl?: string | null;
}

const SignatureBottomSheet = ({
  isOpen,
  onClose,
  onSubmit,
  title = 'Sign here',
  submitLabel = 'Submit',
  previewPdfUrl,
}: SignatureBottomSheetProps) => {
  const sigRef = useRef<SignatureCanvas | null>(null);
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const canvasHeight = previewPdfUrl ? 120 : 200;
  const [canvasWidth, setCanvasWidth] = useState(760);

  useEffect(() => {
    if (!isOpen) {
      setHasDrawn(false);
      setSubmitting(false);
      sigRef.current?.clear();
      return undefined;
    }
    const measure = () => {
      const node = canvasWrapperRef.current;
      if (!node) return;
      const width = node.clientWidth;
      if (width > 0) setCanvasWidth(width);
    };
    const initial = window.setTimeout(measure, 350);
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      if (canvasWrapperRef.current) observer.observe(canvasWrapperRef.current);
    } else {
      window.addEventListener('resize', measure);
    }
    return () => {
      window.clearTimeout(initial);
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', measure);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleEnd = () => {
    if (!sigRef.current) return;
    setHasDrawn(!sigRef.current.isEmpty());
  };

  const handleClear = () => {
    sigRef.current?.clear();
    setHasDrawn(false);
  };

  const buildSignatureDataUrl = (): string => {
    const source = sigRef.current!.getTrimmedCanvas();
    const maxWidth = 600;
    const scale = source.width > maxWidth ? maxWidth / source.width : 1;
    const width = Math.max(1, Math.round(source.width * scale));
    const height = Math.max(1, Math.round(source.height * scale));
    const target = document.createElement('canvas');
    target.width = width;
    target.height = height;
    const context = target.getContext('2d');
    if (!context) return source.toDataURL('image/png');
    context.drawImage(source, 0, 0, width, height);
    return target.toDataURL('image/png');
  };

  const handleSubmit = async () => {
    if (!sigRef.current || sigRef.current.isEmpty() || submitting) return;
    const dataUrl = buildSignatureDataUrl();
    setSubmitting(true);
    try {
      await onSubmit(dataUrl);
    } catch (err: any) {
      console.error(err);
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.message;
      const rawMessage = backendMessage || err?.message || '';
      const isNetworkError =
        !status && (err?.code === 'ERR_NETWORK' || /load failed|failed to fetch|network|timeout/i.test(rawMessage));
      const friendlyMessage = isNetworkError
        ? 'We could not load the form. Please check your internet connection and try again.'
        : rawMessage || 'We could not submit the document. Please try again.';
      const detail = status ? ` (status ${status})` : err?.code ? ` (${err.code})` : '';
      toast.error(`${friendlyMessage}${detail}`);
      setSubmitting(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="sheet-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={submitting ? undefined : onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            zIndex: 9998,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <motion.div
            key="sheet-card"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 900,
              height: previewPdfUrl ? '96vh' : 'auto',
              maxHeight: '96vh',
              display: 'flex',
              flexDirection: 'column',
              background: colors.card,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              padding: '12px 20px 20px',
              boxShadow: '0 -12px 32px rgba(15, 23, 42, 0.18)',
              position: 'relative',
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                background: colors.border,
                borderRadius: 2,
                margin: '0 auto 12px',
                flexShrink: 0,
              }}
            />
            <div
              style={{
                marginBottom: 12,
                flexShrink: 0,
              }}
            >
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{title}</h3>
            </div>
            {previewPdfUrl && (
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: colors.muted, marginBottom: 6, flexShrink: 0 }}>
                  Review the filled document before signing.
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <InlinePdfViewer url={previewPdfUrl} maxHeight="100%" />
                </div>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 6,
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Sign below</span>
              <button
                type="button"
                onClick={handleClear}
                disabled={submitting || !hasDrawn}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: hasDrawn && !submitting ? colors.accent : colors.muted,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: hasDrawn && !submitting ? 'pointer' : 'not-allowed',
                  padding: 0,
                }}
              >
                <i className="fas fa-eraser" style={{ marginRight: 6 }} />
                Clear
              </button>
            </div>
            <div
              ref={canvasWrapperRef}
              style={{
                border: `1px dashed ${colors.border}`,
                borderRadius: 12,
                background: '#fff',
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
                height: canvasHeight,
              }}
            >
              <SignatureCanvas
                ref={sigRef}
                penColor="#111827"
                canvasProps={{
                  width: canvasWidth,
                  height: canvasHeight,
                  style: {
                    width: canvasWidth,
                    height: canvasHeight,
                    display: 'block',
                    pointerEvents: submitting ? 'none' : 'auto',
                  },
                }}
                onEnd={handleEnd}
              />
              {!hasDrawn && !submitting && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    transform: 'translateY(-50%)',
                    textAlign: 'center',
                    color: colors.muted,
                    fontSize: 13,
                    pointerEvents: 'none',
                  }}
                >
                  {previewPdfUrl ? 'Sign here' : 'Sign here with your mouse or finger'}
                </div>
              )}
              {submitting && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(255,255,255,0.85)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    color: colors.muted,
                  }}
                >
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
                  Submitting...
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 16, flexShrink: 0 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                style={{ ...layout.secondaryBtn, flex: 1, justifyContent: 'center', opacity: submitting ? 0.6 : 1 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!hasDrawn || submitting}
                style={{
                  ...(hasDrawn && !submitting ? layout.primaryBtn : layout.disabledBtn),
                  flex: 2,
                }}
              >
                {submitting ? 'Submitting...' : submitLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default SignatureBottomSheet;
